import React, { useState, memo } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Phone, 
  MessageSquare, 
  Camera, 
  Send, 
  FileText,
  ChevronUp,
  ChevronDown,
  Upload,
  Wrench,
  User
} from 'lucide-react';

// WhatsApp icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/**
 * Mobile Sticky Action Bar
 * Shows at bottom of screen on mobile devices for quick actions
 * Touch-optimized with 44px minimum tap targets
 */
function MobileStickyActionBar({ 
  job, 
  onAction, 
  onUploadEvidence,
  onAddNote,
  onOpenComms,
  loading = false,
  className = ""
}) {
  const [expanded, setExpanded] = useState(false);
  
  if (!job) return null;
  
  // Quick contact data
  const residentPhone = job.resident_phone || job.contact_phone || job.phone;
  const contractorPhone = job.contractor_phone || job.assigned_contractor?.phone;
  const residentName = job.resident_name || job.contact_name || 'Resident';
  const contractorName = job.contractor_name || job.assigned_contractor?.company_name || 'Contractor';
  
  const normalizePhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('0')) return '44' + digits.substring(1);
    return digits.startsWith('44') ? digits : '44' + digits;
  };
  
  const openWhatsApp = (phone, name, type) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return;
    const message = type === 'resident'
      ? `Hello ${name}, this is Park Home Support regarding job ${job.reference || job.id}. We have an update for you.`
      : `Hello, this is Park Home Support regarding job ${job.reference || job.id}. Please provide an update when possible.`;
    window.open(`https://wa.me/${normalized}?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  // Primary quick actions (always visible)
  const primaryActions = [
    { 
      id: 'add_note', 
      icon: MessageSquare, 
      label: 'Note',
      color: 'bg-blue-600 active:bg-blue-700',
      onClick: onAddNote
    },
    { 
      id: 'upload', 
      icon: Camera, 
      label: 'Evidence',
      color: 'bg-teal-600 active:bg-teal-700',
      onClick: onUploadEvidence
    },
    { 
      id: 'chase', 
      icon: Send, 
      label: 'Chase',
      color: 'bg-amber-600 active:bg-amber-700',
      onClick: () => onAction?.('chase', job)
    },
    { 
      id: 'comms', 
      icon: FileText, 
      label: 'Comms',
      color: 'bg-slate-600 active:bg-slate-700',
      onClick: onOpenComms
    }
  ];
  
  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 lg:hidden ${className}`}
      data-testid="mobile-sticky-action-bar"
    >
      {/* Expanded Contact Section */}
      {expanded && (
        <div className="bg-white border-t border-slate-200 px-4 py-3 animate-in slide-in-from-bottom-2 duration-200">
          <div className="grid grid-cols-2 gap-3">
            {/* Resident Contact */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <User className="h-3 w-3" />
                {residentName}
              </div>
              {residentPhone ? (
                <div className="flex gap-2">
                  <a 
                    href={`tel:${residentPhone}`}
                    className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-xl bg-slate-100 text-slate-700 text-xs font-medium active:bg-slate-200 touch-manipulation"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </a>
                  <button
                    onClick={() => openWhatsApp(residentPhone, residentName, 'resident')}
                    className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-xl bg-emerald-100 text-emerald-700 text-xs font-medium active:bg-emerald-200 touch-manipulation"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    WhatsApp
                  </button>
                </div>
              ) : (
                <div className="text-xs text-slate-400">No phone</div>
              )}
            </div>
            
            {/* Contractor Contact */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Wrench className="h-3 w-3" />
                {contractorName}
              </div>
              {contractorPhone ? (
                <div className="flex gap-2">
                  <a 
                    href={`tel:${contractorPhone}`}
                    className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-xl bg-slate-100 text-slate-700 text-xs font-medium active:bg-slate-200 touch-manipulation"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </a>
                  <button
                    onClick={() => openWhatsApp(contractorPhone, contractorName, 'contractor')}
                    className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-xl bg-emerald-100 text-emerald-700 text-xs font-medium active:bg-emerald-200 touch-manipulation"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    WhatsApp
                  </button>
                </div>
              ) : (
                <div className="text-xs text-slate-400">Not assigned</div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Main Action Bar */}
      <div className="bg-white border-t border-slate-200 shadow-lg">
        {/* Expand Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center py-1.5 text-slate-400 active:bg-slate-50 touch-manipulation"
          aria-label={expanded ? "Collapse contacts" : "Expand contacts"}
        >
          {expanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronUp className="h-5 w-5" />
          )}
        </button>
        
        {/* Action Buttons */}
        <div className="flex items-center justify-around px-2 pb-safe pb-4">
          {primaryActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={action.onClick}
                disabled={loading}
                className={`flex flex-col items-center justify-center min-w-[64px] min-h-[56px] px-3 py-2 rounded-xl ${action.color} text-white touch-manipulation transition-transform active:scale-95`}
                data-testid={`mobile-action-${action.id}`}
              >
                <Icon className="h-5 w-5 mb-1" />
                <span className="text-[10px] font-medium">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default memo(MobileStickyActionBar);
